import { ArrowRight, Plus, Sparkles } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../../contexts/JourneyContext';
import api from '../../services/api';
import './Forms.css';

interface Employment {
  id: string;
  company: string;
  position: string;
  location: string;
  startDate: string;
  endDate: string;
  current: boolean;
  description: string;
}

interface Certification {
  id: string;
  name: string;
  issuer: string;
  dateObtained: string;
  expiryDate?: string;
  credentialId?: string;
}

const EmploymentForm: React.FC = () => {
  const navigate = useNavigate();
  const { currentJourney, updateJourneyData, setCurrentJourney } = useJourney();
  
  const [employmentList, setEmploymentList] = useState<Employment[]>([{
    id: crypto.randomUUID(),
    company: '',
    position: '',
    location: '',
    startDate: '',
    endDate: '',
    current: false,
    description: '',
  }]);

  const [certificationList, setCertificationList] = useState<Certification[]>([]);
  
  const [enhancingId, setEnhancingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (currentJourney?.collectedData) {
      if (currentJourney.collectedData.employment && currentJourney.collectedData.employment.length > 0) {
        setEmploymentList(currentJourney.collectedData.employment.map((job: any) => ({
          id: job.id || crypto.randomUUID(),
          company: job.company || '',
          position: job.position || '',
          location: job.location || '',
          startDate: job.startDate || '',
          endDate: job.endDate || '',
          current: job.current || false,
          description: job.description || '',
        })));
      }

      if (currentJourney.collectedData.certifications) {
        setCertificationList(currentJourney.collectedData.certifications.map((cert: any) => ({
          id: cert.id || crypto.randomUUID(),
          name: cert.name || '',
          issuer: cert.issuer || '',
          dateObtained: cert.dateObtained || '',
          expiryDate: cert.expiryDate || '',
          credentialId: cert.credentialId || '',
        })));
      }
    }
  }, [currentJourney]);

  const handleEmploymentChange = (id: string, field: keyof Employment, value: string | boolean) => {
    setEmploymentList(prev => prev.map(job =>
      job.id === id ? { ...job, [field]: value } : job
    ));
  };

  const handleCertificationChange = (id: string, field: keyof Certification, value: string) => {
    setCertificationList(prev => prev.map(cert =>
      cert.id === id ? { ...cert, [field]: value } : cert
    ));
  };

  const addEmployment = () => {
    setEmploymentList(prev => [...prev, {
      id: crypto.randomUUID(),
      company: '',
      position: '',
      location: '',
      startDate: '',
      endDate: '',
      current: false,
      description: '',
    }]);
  };

  const removeEmployment = (id: string) => {
    if (employmentList.length > 1) {
      setEmploymentList(prev => prev.filter(job => job.id !== id));
    }
  };

  const addCertification = () => {
    setCertificationList(prev => [...prev, {
      id: crypto.randomUUID(),
      name: '',
      issuer: '',
      dateObtained: '',
      expiryDate: '',
      credentialId: '',
    }]);
  };

  const removeCertification = (id: string) => {
    setCertificationList(prev => prev.filter(cert => cert.id !== id));
  };

  const enhanceDescription = async (id: string) => {
    const job = employmentList.find(j => j.id === id);
    if (!job || !job.description.trim()) {
      alert('Please enter a description first');
      return;
    }

    if (!currentJourney) return;

    setEnhancingId(id);
    try {
      const response = await api.enhanceText(currentJourney.id, job.description, 'job_description');
      setEmploymentList(prev => prev.map(j =>
        j.id === id ? { ...j, description: response.enhancedText } : j
      ));
    } catch (error) {
      console.error('Failed to enhance:', error);
      alert('Failed to enhance text. Please try again.');
    } finally {
      setEnhancingId(null);
    }
  };

  const validateForm = (): boolean => {
    for (const job of employmentList) {
      if (!job.company.trim() || !job.position.trim() || !job.startDate) {
        alert('Please fill in all required employment fields (Company, Position, Start Date)');
        return false;
      }
      if (!job.current && !job.endDate) {
        alert('Please provide an end date or mark the position as current');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await updateJourneyData({
        employment: employmentList,
        certifications: certificationList,
      });
      
      // Update journey stage to 3 (Projects - first voice stage)
      if (currentJourney) {
        await api.updateJourneyStage(currentJourney.id, 3);
        // Update local state
        setCurrentJourney({
          ...currentJourney,
          currentStage: 3,
        });
      }
      
      // Move to next stage (Projects - voice stage)
      navigate('/journey/stages');
    } catch (error) {
      console.error('Failed to save employment:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <div className="stage-indicator">
          <span className="stage-number">Stage 3 of 5</span>
          <div className="progress-bar-thin">
            <div className="progress-fill-thin" style={{ width: '60%' }} />
          </div>
        </div>
        <h1>Employment & Certifications</h1>
        <p className="form-subtitle">Share your work experience and professional certifications</p>
      </div>

      <form onSubmit={handleSubmit} className="resume-form">
        {/* Employment Section */}
        <div className="form-section">
          <h2>Work Experience</h2>
          <div className="repeatable-cards">
            {employmentList.map((job, index) => (
              <div key={job.id} className="card">
                <div className="card-header">
                  <h3 className="card-title">Position #{index + 1}</h3>
                  {employmentList.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeEmployment(job.id)}
                      className="btn-icon btn-remove"
                      title="Remove"
                    >
                      🗑️
                    </button>
                  )}
                </div>

                <div className="form-row two-columns">
                  <div className="form-field">
                    <label>Company *</label>
                    <input
                      type="text"
                      value={job.company}
                      onChange={(e) => handleEmploymentChange(job.id, 'company', e.target.value)}
                      placeholder="Google Inc."
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>Position *</label>
                    <input
                      type="text"
                      value={job.position}
                      onChange={(e) => handleEmploymentChange(job.id, 'position', e.target.value)}
                      placeholder="Senior Software Engineer"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label>Location</label>
                    <input
                      type="text"
                      value={job.location}
                      onChange={(e) => handleEmploymentChange(job.id, 'location', e.target.value)}
                      placeholder="Mountain View, CA"
                    />
                  </div>
                </div>

                <div className="form-row two-columns">
                  <div className="form-field">
                    <label>Start Date *</label>
                    <input
                      type="month"
                      value={job.startDate}
                      onChange={(e) => handleEmploymentChange(job.id, 'startDate', e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>End Date {!job.current && '*'}</label>
                    <input
                      type="month"
                      value={job.endDate}
                      onChange={(e) => handleEmploymentChange(job.id, 'endDate', e.target.value)}
                      disabled={job.current}
                      required={!job.current}
                    />
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id={`current-${job.id}`}
                        checked={job.current}
                        onChange={(e) => handleEmploymentChange(job.id, 'current', e.target.checked)}
                      />
                      <label htmlFor={`current-${job.id}`}>I currently work here</label>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label>Description & Achievements</label>
                    <div className="enhance-wrapper">
                      <textarea
                        value={job.description}
                        onChange={(e) => handleEmploymentChange(job.id, 'description', e.target.value)}
                        placeholder="Built APIs, led team of 5, improved performance by 40%..."
                        className="enhance-textarea"
                        rows={4}
                      />
                      <button
                        type="button"
                        onClick={() => enhanceDescription(job.id)}
                        disabled={enhancingId === job.id || !job.description.trim()}
                        className="btn-enhance"
                      >
                        {enhancingId === job.id ? (
                          <>
                            <Sparkles size={16} />
                            Enhancing...
                          </>
                        ) : (
                          <>
                            <Sparkles size={16} />
                            Enhance with AI
                          </>
                        )}
                      </button>
                    </div>
                    <small className="field-hint">
                      Write your responsibilities and achievements in bullet points or sentences. 
                      Click "Enhance with AI" to make them ATS-friendly and impactful!
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={addEmployment}
            className="btn-add"
          >
            <Plus size={16} />
            Add Another Position
          </button>
        </div>

        {/* Certifications Section */}
        <div className="form-section">
          <h2>Professional Certifications <span className="optional">(Optional)</span></h2>
          
          {certificationList.length === 0 ? (
            <button
              type="button"
              onClick={addCertification}
              className="btn-add"
            >
              <Plus size={16} />
              Add Certification
            </button>
          ) : (
            <>
              <div className="repeatable-cards">
                {certificationList.map((cert, index) => (
                  <div key={cert.id} className="card">
                    <div className="card-header">
                      <h3 className="card-title">Certification #{index + 1}</h3>
                      <button
                        type="button"
                        onClick={() => removeCertification(cert.id)}
                        className="btn-icon btn-remove"
                        title="Remove"
                      >
                        🗑️
                      </button>
                    </div>

                    <div className="form-row two-columns">
                      <div className="form-field">
                        <label>Certification Name</label>
                        <input
                          type="text"
                          value={cert.name}
                          onChange={(e) => handleCertificationChange(cert.id, 'name', e.target.value)}
                          placeholder="AWS Certified Solutions Architect"
                        />
                      </div>

                      <div className="form-field">
                        <label>Issuing Organization</label>
                        <input
                          type="text"
                          value={cert.issuer}
                          onChange={(e) => handleCertificationChange(cert.id, 'issuer', e.target.value)}
                          placeholder="Amazon Web Services"
                        />
                      </div>
                    </div>

                    <div className="form-row two-columns">
                      <div className="form-field">
                        <label>Date Obtained</label>
                        <input
                          type="month"
                          value={cert.dateObtained}
                          onChange={(e) => handleCertificationChange(cert.id, 'dateObtained', e.target.value)}
                        />
                      </div>

                      <div className="form-field">
                        <label>Expiry Date <span className="optional">(if applicable)</span></label>
                        <input
                          type="month"
                          value={cert.expiryDate}
                          onChange={(e) => handleCertificationChange(cert.id, 'expiryDate', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-field">
                        <label>Credential ID <span className="optional">(Optional)</span></label>
                        <input
                          type="text"
                          value={cert.credentialId}
                          onChange={(e) => handleCertificationChange(cert.id, 'credentialId', e.target.value)}
                          placeholder="ABC123XYZ789"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addCertification}
                className="btn-add"
              >
                <span>➕</span> Add Another Certification
              </button>
            </>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/journey/forms/education')}
            className="btn-secondary"
          >
            ← Back to Education
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary"
          >
            {isSaving ? 'Saving...' : (
              <>
                Continue to Projects Deep-Dive
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmploymentForm;

